/**
 * Turn the binary calculator's raw text fields into a result or a named error, so the
 * widget only has to pick the sentence for the error.
 */

import { parseIntegerInput } from "@/lib/parse-number";
import {
  type ArithOp,
  type BitwiseOp,
  bitwise,
  calculate,
  isShiftOp,
} from "./binary-calc";
import { type ParsedBinary, parseBinary } from "./bits";

export type EvalErrorKey =
  | "errEmptyA"
  | "errEmptyB"
  | "errInvalidA"
  | "errInvalidB"
  | "errNegativeBitwise"
  | "errDivideZero"
  | "errTooWide"
  | "errCount";

export type ArithOk = Extract<ReturnType<typeof calculate>, { ok: true }>;
export type BitsOk = Extract<ReturnType<typeof bitwise>, { ok: true }>;

export type Evaluation =
  | { kind: "error"; key: EvalErrorKey }
  | { kind: "arith"; outcome: ArithOk }
  | { kind: "bits"; outcome: BitsOk };

export interface EvalInput {
  mode: "arithmetic" | "bitwise";
  arithOp: ArithOp;
  bitOp: BitwiseOp;
  aText: string;
  bText: string;
  countText: string;
  width: number;
}

/** Whether the current operation reads the second number. */
export function usesSecondOperand(input: EvalInput): boolean {
  return (
    input.mode === "arithmetic" ||
    (input.bitOp !== "not" && !isShiftOp(input.bitOp))
  );
}

/** Whether the current operation reads a shift or rotate distance. */
export function usesCount(input: EvalInput): boolean {
  return input.mode === "bitwise" && isShiftOp(input.bitOp);
}

function operandError(
  parsed: ParsedBinary,
  empty: EvalErrorKey,
  invalid: EvalErrorKey
): EvalErrorKey | null {
  if (parsed.ok) {
    return null;
  }
  return parsed.error === "empty" ? empty : invalid;
}

function bitwiseEvaluation(
  input: EvalInput,
  a: Extract<ParsedBinary, { ok: true }>,
  b: bigint,
  bNegative: boolean
): Evaluation {
  if (a.negative || bNegative) {
    return { kind: "error", key: "errNegativeBitwise" };
  }
  const count = parseIntegerInput(input.countText);
  if (usesCount(input) && (count === null || count < 0)) {
    return { kind: "error", key: "errCount" };
  }
  const outcome = bitwise(input.bitOp, a.value, b, count ?? 0, input.width);
  if (outcome.ok) {
    return { kind: "bits", outcome };
  }
  return {
    kind: "error",
    key: outcome.error === "operandTooWide" ? "errTooWide" : "errCount",
  };
}

export function evaluateBinary(input: EvalInput): Evaluation {
  const a = parseBinary(input.aText);
  const b = parseBinary(input.bText);
  const needsB = usesSecondOperand(input);
  if (!a.ok) {
    return {
      kind: "error",
      key: operandError(a, "errEmptyA", "errInvalidA") ?? "errInvalidA",
    };
  }
  if (needsB && !b.ok) {
    return {
      kind: "error",
      key: operandError(b, "errEmptyB", "errInvalidB") ?? "errInvalidB",
    };
  }
  const bValue = b.ok ? b.value : BigInt(0);
  if (input.mode === "arithmetic") {
    const outcome = calculate(input.arithOp, a.value, bValue);
    return outcome.ok
      ? { kind: "arith", outcome }
      : { kind: "error", key: "errDivideZero" };
  }
  return bitwiseEvaluation(input, a, bValue, needsB && b.ok && b.negative);
}
